import React from 'react';
import { Text, View } from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';

import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useResponsive } from '../../../hooks';
import { getFlexRow } from '../../../utils/rtl';

import type {
  PageHeaderProps,
  PageHeaderVariant,
} from './PageHeader.types';

interface PageHeaderStyleResult {
  container: ViewStyle;
}

interface ThemeColors {
  card: string;
  cardBorder: string;
  textPrimary: string;
  textMuted: string;
}

const styleCache = new Map<string, PageHeaderStyleResult>();

function getPageHeaderStyles(
  variant: PageHeaderVariant,
  theme: ThemeColors,
): PageHeaderStyleResult {
  const cacheKey = `${variant}:${theme.card}:${theme.cardBorder}`;
  const cached = styleCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let container: ViewStyle;

  switch (variant) {
    case 'card':
      container = {
        backgroundColor: theme.card,
        borderWidth: tokens.spacing.absolute["px"],
        borderColor: theme.cardBorder,
        borderRadius: tokens.radii.absolute["radius-12"],
        paddingHorizontal: tokens.spacing.absolute["6"],
        paddingVertical: tokens.spacing.absolute["5"],
        marginBottom: tokens.spacing.absolute["6"],
      };
      break;

    case 'plain':
      container = {};
      break;
  }

  const result: PageHeaderStyleResult = { container };
  styleCache.set(cacheKey, result);
  return result;
}

export function PageHeader(props: PageHeaderProps) {
  const {
    title,
    description,
    badge,
    actions,
    contentTrailing,
    variant = 'card',
    style,
    testID,
  } = props;

  const { language } = useLanguage();
  const { colors } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const isRTL = language === 'ar';

  const themeColors: ThemeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
  };

  const styles = getPageHeaderStyles(variant, themeColors);

  // Layout rules:
  // - Desktop (≥1024): row with title-start / actions-end.
  // - Tablet (640–1023): row with flex-wrap so 3 actions can drop.
  // - Mobile (<640): column, title-first (industry standard, all five
  //   surveyed design systems). Row reversal rejected per spec.
  const titleRowDirection: ViewStyle['flexDirection'] = isMobile
    ? 'column'
    : getFlexRow(isRTL);

  const titleRowStyle: ViewStyle = {
    flexDirection: titleRowDirection,
    alignItems: isMobile ? 'stretch' : 'center',
    justifyContent: 'space-between',
    flexWrap: isTablet ? 'wrap' : 'nowrap',
    gap: tokens.spacing.absolute["3"],
  };

  const titleBlockStyle: ViewStyle = {
    flexDirection: getFlexRow(isRTL),
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["2"],
    flexShrink: 1,
  };

  const titleStackStyle: ViewStyle = {
    flexDirection: 'column',
    flexShrink: 1,
  };

  const titleStyle: TextStyle = {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography["heading 4"]["font size"],
    color: themeColors.textPrimary,
    textAlign: isRTL ? 'right' : 'left',
  };

  const descriptionStyle: TextStyle = {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: themeColors.textMuted,
    textAlign: isRTL ? 'right' : 'left',
    marginTop: tokens.spacing.absolute["1"],
  };

  return (
    <View style={[styles.container, style]} testID={testID}>
      <View style={titleRowStyle}>
        <View style={titleBlockStyle}>
          <View style={titleStackStyle}>
            <Text
              accessibilityRole="header"
              aria-level={1}
              style={titleStyle}
            >
              {title}
            </Text>
            {description ? (
              <Text style={descriptionStyle}>{description}</Text>
            ) : null}
          </View>
          {badge}
        </View>
        {actions ? <View>{actions}</View> : null}
      </View>
      {contentTrailing ? (
        <View style={{ marginTop: tokens.spacing.absolute["4"] }}>
          {contentTrailing}
        </View>
      ) : null}
    </View>
  );
}
